/**
 * Customer proposals — client service. Wraps the `features/proposal`
 * Cloud Functions.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createCallable } from '../../../firebaseFunctions';

const draftProposalNarrativeFn = createCallable('draftProposalNarrative');
const saveProposalShareFn = createCallable('saveProposalShare');
const resolveProposalShareFn = createCallable('resolveProposalShare');
const revokeProposalShareFn = createCallable('revokeProposalShare');
const listProposalSharesFn = createCallable('listProposalShares');
const renderProposalShotsFn = createCallable('renderProposalShots', {
  timeout: 540000,
});

export const proposalService = {
  /**
   * Draft the narrative section from the proposal's facts.
   * @returns {Promise<{headline, intro, highlights: {title, detail}[], closing}>}
   */
  async draftNarrative(projectId, facts) {
    const res = await draftProposalNarrativeFn({ projectId, facts });
    return res.data.narrative;
  },

  /**
   * Create (no token) or update (token) the share record behind a
   * proposal's link. Resolves the token.
   */
  async saveShare({ projectId, structureId, token = null, proposal }) {
    const res = await saveProposalShareFn({
      projectId,
      structureId,
      token,
      proposal,
    });
    return res.data.token;
  },

  /**
   * Visitor: open a proposal link. Must be signed in
   * (`authService.ensureShareSession`) — the callable grants that uid the
   * structure read the 3D walkthrough needs.
   * @returns {Promise<{ proposal: Object, structureId: string }>}
   */
  async resolveShare(token) {
    const res = await resolveProposalShareFn({ token });
    return res.data;
  },

  /** Owner: turn a proposal link off (and take back its walkthrough access). */
  async revokeShare(projectId, token) {
    await revokeProposalShareFn({ projectId, token });
  },

  /**
   * Owner: a project's proposal links — whether each is on, when and to whom
   * it was last sent with an estimate (both null if never), its title, when
   * it was made, and whether it has a PDF to attach.
   * @returns {Promise<{ token: string, enabled: boolean, sentAt: number|null, sentTo: string|null, title: string|null, createdAt: number|null, hasPdf: boolean, estimateId: string|null, structureId: string|null, sentWithEstimateId: string|null }[]>}
   */
  async listShares(projectId) {
    const res = await listProposalSharesFn({ projectId });
    return res.data.shares;
  },

  /**
   * Owner: render uploaded captures on the server as process run `runId`. The
   * call lasts as long as the renders (minutes) — follow the run with
   * `subscribeRun` instead of waiting on it.
   * @param {string} structureId
   * @param {string} runId - minted by the caller, so it can follow the run first
   * @param {{ storagePath, caption, roomId, roomName }[]} shots
   */
  async renderShots(structureId, runId, shots) {
    await renderProposalShotsFn({ structureId, runId, shots });
  },

  /**
   * Follow a process run (`structures/{id}/processRuns/{runId}`).
   * @returns {() => void} unsubscribe
   */
  subscribeRun(structureId, runId, onRun, onError) {
    return onSnapshot(
      doc(db, 'structures', structureId, 'processRuns', runId),
      (snap) => snap.exists() && onRun(snap.data()),
      onError
    );
  },
};

/** The public URL a proposal's link and QR code point at. */
export const proposalShareUrl = (token, origin = window.location.origin) =>
  `${origin}/p/${token}`;
